// linkedAuditEvent.js — pure (JSX-free) logic for the "Link source audit event"
// feature on Microsoft-security pass-through alerts
// (rule_defender_endpoint_alert_passthrough — e.g. "Mailbox Permission Change").
//
// The Defender payload names only the actor; what actually changed (target
// mailbox, trustee, access rights) lives only in the Exchange admin audit event we
// ingest. The LinkSourceAuditEvent endpoint correlates the alert to that event and
// stamps a `_linked_audit_event` object. This module extracts the correlation
// inputs from the alert's raw event and resolves which link to render.
// (handoff 2026-07-11_Alert-to-Audit-Event-Linking-Portal-Handoff)
//
// `_linked_audit_event` shape (snake_case — identical for the inline pipeline stamp
// and the portal write-back, so both render through one path):
//   { link_confidence, candidate_count, linked_source_event_id, linked_operation,
//     actor_upn, actor_object_id, changed_mailbox_upn, changed_mailbox_object_id,
//     changed_mailbox_account_enabled, trustee_upn, trustee_object_id,
//     access_rights, inheritance_type, source_client_ip, source_creation_time,
//     linked_by, linked_utc }

// Defender alert titles that can be linked (mirrors the hardcoded PS map in
// Shared/AuditEventLinkHelper.psm1 — display/gating only; the backend is authoritative).
// Compared case-insensitively.
export const LINKABLE_TITLES = new Set([
  'mailbox permission change',
  'mailbox permission change (sendas)',
  'mailbox folder permission change',
]);

function safeParse(s) {
  if (!s || typeof s !== 'string') return null;
  try { return JSON.parse(s); } catch { return null; }
}

// Pull the correlation inputs from the alert's parsed raw_event_json.
//   actorObjectId — first evidence[].userAccount.azureAdUserId (Defender pass-through
//     evidence). Robust to UPN renames; the backend falls back to actorUpn.
//   alertTitle — top-level $.title (drives the operation map).
//   winStart/winEnd — $.firstActivityDateTime / $.lastActivityDateTime.
// `canLink` gates the button: a known-linkable title + an activity window present.
export function extractLinkInputs(rawEvent) {
  const empty = { actorObjectId: null, actorUpn: null, alertTitle: null, winStart: null, winEnd: null, canLink: false };
  if (!rawEvent || typeof rawEvent !== 'object') return empty;

  let actorObjectId = null;
  let actorUpn = null;
  const ev = Array.isArray(rawEvent.evidence) ? rawEvent.evidence : [];
  for (const e of ev) {
    const ua = e?.userAccount;
    if (!ua) continue;
    if (!actorObjectId && ua.azureAdUserId) actorObjectId = String(ua.azureAdUserId);
    if (!actorUpn && ua.userPrincipalName) actorUpn = String(ua.userPrincipalName);
    if (actorObjectId && actorUpn) break;
  }

  const alertTitle = rawEvent.title ? String(rawEvent.title) : null;
  const winStart = rawEvent.firstActivityDateTime ? String(rawEvent.firstActivityDateTime) : null;
  const winEnd = rawEvent.lastActivityDateTime ? String(rawEvent.lastActivityDateTime) : (winStart || null);

  const canLink = !!(alertTitle && LINKABLE_TITLES.has(alertTitle.trim().toLowerCase()) && winStart);
  return { actorObjectId, actorUpn, alertTitle, winStart, winEnd, canLink };
}

// A resolved link is "renderable" when it carries either a single event
// (linked_source_event_id) or a multi-event envelope (events[]).
function hasLinkedDetail(obj) {
  if (!obj || typeof obj !== 'object') return false;
  if (obj.linked_source_event_id) return true;
  if (Array.isArray(obj.events) && obj.events.length > 0) return true;
  return false;
}

// Resolve which linked-audit-event object to render:
//   1) a portal write-back persisted on the row (linkedAuditEventJson) wins —
//      either a single-event object or a multi-event envelope { events:[…] };
//   2) else the pipeline's inline `_linked_audit_event` on the raw event
//      (only when it actually resolved — link_confidence 'unique', single event).
// `rawEvent` is the parsed raw_event_json (may be null at the list level).
export function effectiveLinkedAuditEvent(alert, rawEvent) {
  const persisted = safeParse(alert?.linkedAuditEventJson);
  if (hasLinkedDetail(persisted)) return { ...persisted, _source: 'portal' };

  const inline = rawEvent && typeof rawEvent === 'object' ? rawEvent._linked_audit_event : null;
  if (inline && inline.link_confidence === 'unique' && inline.linked_source_event_id) {
    return { ...inline, _source: 'inline' };
  }
  return null;
}

// Flatten a resolved link object into an array of per-event detail objects:
//   - multi envelope { events:[…] }  -> the events array
//   - single flat object            -> [ the object ]
// Each element carries changed_mailbox_upn / trustee_upn / access_rights /
// inheritance_type / linked_operation / source_client_ip / source_creation_time /
// linked_source_event_id (+ changed_mailbox_account_enabled on the mailbox).
export function linkedEventList(link) {
  if (!link || typeof link !== 'object') return [];
  if (Array.isArray(link.events)) return link.events.filter(Boolean);
  if (link.linked_source_event_id) return [link];
  return [];
}
